import { useState } from 'react';
import { Link } from 'react-router-dom';
import { seatLabel, type EventAttendee } from '@ni/shared';
import { PeoplePicker, type PickedPerson } from '../../components/reports/people-picker';
import {
  useAddEventPeople,
  useEventAttendees,
  useDeleteEventAttendees,
  useEventRunStateInvalidation,
} from '../../api/event-hooks';
import { useRun } from '../../api/hooks';
import { RunBlock } from '../../components/run-strip/run-block';
import { Modal } from '../../components/ui/modal';
import { OverflowMenu } from '../../components/ui/overflow-menu';
import { RecordTable } from '../../components/record/record-table';
import { ErrorLine } from '../../components/ui/error-line';
import { WorkingButton } from '../../components/ui/working-button';
import { IfWrites } from '../../components/write-access';
import {
  EVENT_ATTENDEES_ALL_FILTER,
  EVENT_ATTENDEES_COLUMNS,
  EVENT_ATTENDEES_DELETE,
  EVENT_ATTENDEES_DELETE_LINE,
  EVENT_ATTENDEES_DELETING,
  EVENT_ATTENDEES_KEEP,
  EVENT_ATTENDEES_REVIEW_FILTER,
  EVENT_ATTENDEE_NEEDS_REVIEW,
  EVENT_ATTENDEES_EMPTY,
  EVENT_ATTENDEES_SEARCH,
  EVENT_ATTENDEES_UPLOAD,
  EVENT_ATTENDEES_UPLOAD_MORE,
  EVENT_ATTENDEES_UPLOAD_PLACEHOLDER,
  EVENT_ATTENDEES_UPLOAD_SUBMIT,
  EVENT_ATTENDEES_UPLOAD_TITLE,
  EVENT_ATTENDEE_STATUS_LABELS,
  EVENT_ATTENDEE_RATED_FROM_RECORD,
  LOADING_MORE,
  LOAD_MORE,
  ORGANISATIONS_NONE_MATCH,
  EVENT_ADD_PEOPLE,
  EVENT_ADD_PEOPLE_LINE,
  EVENT_ADD_PEOPLE_SUBMIT,
  EVENT_ADD_PEOPLE_WORKING,
  eventAttendeesCountLine,
  eventOpenUploadLine,
} from '../../lib/copy';
import { SEARCH_DEBOUNCE_MS, useSettled } from '../../lib/debounce';
import { NewImport } from '../imports';
import { AttendeeModal } from './attendee-modal';
import { useEventRecord } from './record';
import { Loading } from '../../components/ui/loading';
import { EmptyLine } from '../../components/ui/empty-line';
import { Button } from '../../components/ui/button';

/**
 * **The Attendees tab** (event graph design §3a): the event's graph. Uploading
 * a list is optional and opens the import flow aimed at this event; a later
 * upload adds to the same list. Nothing here is on the record until it is
 * promoted. The scan and the reports start from the Analysis tab, from the
 * analysis that needs them (docs/notes/2026-09-24-event-analysis-flow-spec.md).
 */
export function EventAttendeesTab() {
  const { entityId, record } = useEventRecord();
  const accountId = record.subject.account?.id ?? null;
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<PickedPerson[]>([]);
  const add = useAddEventPeople(entityId);
  const [uploading, setUploading] = useState(false);
  const [typed, setTyped] = useState('');
  const [q, search] = useSettled('', SEARCH_DEBOUNCE_MS);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const asked = q.trim();
  const attendees = useEventAttendees(entityId, {
    ...(asked ? { q: asked } : {}),
    ...(reviewOnly ? { review: 'true' as const } : {}),
  });

  const pages = attendees.data?.pages ?? [];
  const counts = pages[0]?.counts ?? null;
  const scan = pages[0]?.scan ?? null;
  const openUploads = pages[0]?.open_uploads ?? [];
  const rows = pages.flatMap((page) => page.attendees);
  const hasList = (counts?.total ?? 0) > 0;

  const [deleting, setDeleting] = useState(false);
  const remove = useDeleteEventAttendees(entityId);
  const activeRun = useRun(scan?.active_run_id ?? null);
  useEventRunStateInvalidation(activeRun.data);

  return (
    <>
      <div className="list-toolbar">
        {hasList ? (
          <div className="field search-field">
            <label className="label" htmlFor="attendees-q">
              {EVENT_ATTENDEES_SEARCH}
            </label>
            <input
              id="attendees-q"
              type="search"
              autoComplete="off"
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value);
                search(event.target.value);
              }}
            />
          </div>
        ) : null}
        <div className="toolbar-actions">
          <IfWrites>
            <button
              type="button"
              className={hasList ? 'btn act' : 'btn go'}
              onClick={() => setUploading(true)}
            >
              {hasList ? EVENT_ATTENDEES_UPLOAD_MORE : EVENT_ATTENDEES_UPLOAD}
            </button>
            <Button variant="act" onClick={() => setAdding(true)}>
              {EVENT_ADD_PEOPLE}
            </Button>
            {/* Deleting the list is an ordinary delete, kept out of the way. */}
            {hasList && !scan?.active_run_id ? (
              <OverflowMenu>
                <button
                  type="button"
                  role="menuitem"
                  className="overflow-menu-item"
                  onClick={() => setDeleting(true)}
                >
                  {EVENT_ATTENDEES_DELETE}
                </button>
              </OverflowMenu>
            ) : null}
          </IfWrites>
        </div>
      </div>

      {scan?.active_run_id && activeRun.data ? (
        <IfWrites>
          <RunBlock
            subjectId={entityId}
            run={activeRun.data}
            pendingDecisions={[]}
            pollFailures={activeRun.failureCount}
          />
        </IfWrites>
      ) : null}

      {/* An upload stops at its plan until somebody imports it, and until then
          this list does not have its people: say so here, where it was started. */}
      {openUploads.length > 0 ? (
        <div className="section open-uploads">
          {openUploads.map((upload) => (
            <div key={upload.import_id} className="note-line">
              <Link className="link" to={`/imports/${upload.import_id}`}>
                {upload.filename}
              </Link>{' '}
              {eventOpenUploadLine(upload.phase)}
            </div>
          ))}
        </div>
      ) : null}

      {hasList && (counts?.needs_review ?? 0) > 0 ? (
        <div className="form-actions">
          <button
            type="button"
            className={reviewOnly ? 'chip on' : 'chip'}
            onClick={() => setReviewOnly(true)}
          >
            {EVENT_ATTENDEES_REVIEW_FILTER} ({counts?.needs_review})
          </button>
          <button
            type="button"
            className={reviewOnly ? 'chip' : 'chip on'}
            onClick={() => setReviewOnly(false)}
          >
            {EVENT_ATTENDEES_ALL_FILTER}
          </button>
        </div>
      ) : null}

      <div className="section">
        {attendees.isPending ? (
          <EmptyLine>
            <Loading />
          </EmptyLine>
        ) : rows.length === 0 ? (
          attendees.isError ? (
            <ErrorLine error={attendees.error} load />
          ) : (
            <EmptyLine>{asked ? ORGANISATIONS_NONE_MATCH : EVENT_ATTENDEES_EMPTY}</EmptyLine>
          )
        ) : (
          <>
            {counts ? (
              <div className="src list-count">
                {eventAttendeesCountLine(counts.total, counts.linked)}
              </div>
            ) : null}
            <AttendeesTable rows={rows} seated={(counts?.seated ?? 0) > 0} onOpen={setOpen} />
            {attendees.hasNextPage ? (
              <div className="list-foot">
                <WorkingButton
                  label={LOAD_MORE}
                  workingLabel={LOADING_MORE}
                  working={attendees.isFetchingNextPage}
                  variant="act"
                  tiny
                  onClick={() => void attendees.fetchNextPage()}
                />
              </div>
            ) : null}
          </>
        )}
      </div>

      {deleting ? (
        <Modal label={EVENT_ATTENDEES_DELETE} narrow onClose={() => setDeleting(false)}>
          <div className="modal-head">{EVENT_ATTENDEES_DELETE}</div>
          <div className="modal-line">{EVENT_ATTENDEES_DELETE_LINE}</div>
          {remove.isError ? <ErrorLine error={remove.error} /> : null}
          <div className="modal-actions">
            <WorkingButton
              label={EVENT_ATTENDEES_DELETE}
              workingLabel={EVENT_ATTENDEES_DELETING}
              variant="danger"
              working={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: () => setDeleting(false) })}
            />
            <Button variant="act" onClick={() => setDeleting(false)}>
              {EVENT_ATTENDEES_KEEP}
            </Button>
          </div>
        </Modal>
      ) : null}

      {open ? (
        <AttendeeModal eventId={entityId} nodeId={open} onClose={() => setOpen(null)} />
      ) : null}

      {adding ? (
        <Modal
          label={EVENT_ADD_PEOPLE}
          onClose={() => {
            setAdding(false);
            setPicked([]);
          }}
        >
          <div className="modal-head">{EVENT_ADD_PEOPLE}</div>
          <div className="modal-line">{EVENT_ADD_PEOPLE_LINE}</div>
          <PeoplePicker
            label={EVENT_ADD_PEOPLE}
            value={picked}
            onChange={setPicked}
            max={100}
            {...(accountId ? { accountId } : {})}
          />
          {add.isError ? <ErrorLine error={add.error} /> : null}
          <div className="modal-actions">
            <WorkingButton
              label={EVENT_ADD_PEOPLE_SUBMIT}
              workingLabel={EVENT_ADD_PEOPLE_WORKING}
              working={add.isPending}
              disabled={picked.length === 0}
              onClick={() =>
                add.mutate(
                  picked.map((p) => p.entity_id),
                  {
                    onSuccess: () => {
                      setAdding(false);
                      setPicked([]);
                    },
                  },
                )
              }
            />
          </div>
        </Modal>
      ) : null}

      {uploading ? (
        <NewImport
          eventId={entityId}
          title={EVENT_ATTENDEES_UPLOAD_TITLE}
          submitLabel={EVENT_ATTENDEES_UPLOAD_SUBMIT}
          placeholder={EVENT_ATTENDEES_UPLOAD_PLACEHOLDER}
          offerReplace={hasList}
          onClose={() => setUploading(false)}
        />
      ) : null}
    </>
  );
}

/** "dana@example.org · Acme · Austin": what the list supplied, in one line. */
export function attendeeDetails(identifiers: Readonly<Record<string, string>>): string {
  return ['email', 'phone', 'employer', 'title', 'city']
    .map((key) => identifiers[key])
    .filter((value): value is string => Boolean(value))
    .join(' · ');
}

/**
 * The attendee rows, the same in every vertical: what an analysis thinks of
 * each attendee lives on the Analysis tab, not in a column here. A Seat column
 * appears when the list seats anyone. On a narrow screen each row becomes a
 * card (the `data-label` cells).
 */
export function AttendeesTable({
  rows,
  seated,
  onOpen,
}: {
  rows: readonly EventAttendee[];
  /** The list seats someone: a Seat column, whichever rows are loaded. */
  seated: boolean;
  onOpen: (nodeId: string) => void;
}) {
  return (
    <RecordTable
      className="attendee-table"
      headings={[
        EVENT_ATTENDEES_COLUMNS.name,
        EVENT_ATTENDEES_COLUMNS.details,
        ...(seated ? [EVENT_ATTENDEES_COLUMNS.seat] : []),
        EVENT_ATTENDEES_COLUMNS.record,
        EVENT_ATTENDEES_COLUMNS.status,
      ]}
    >
      {rows.map((row) => {
        const onRecord = row.on_record;
        const details = attendeeDetails(row.identifiers);
        const seat = seatLabel(row.identifiers);
        return (
          <tr key={row.id}>
            <td className="attendee-name">
              <button type="button" className="linkish flush" onClick={() => onOpen(row.id)}>
                {row.name}
              </button>
            </td>
            <td
              className="quiet-cell"
              data-label={EVENT_ATTENDEES_COLUMNS.details}
              data-empty={details ? undefined : ''}
            >
              {details}
            </td>
            {seated ? (
              <td
                className="quiet-cell"
                data-label={EVENT_ATTENDEES_COLUMNS.seat}
                data-empty={seat ? undefined : ''}
              >
                {seat}
              </td>
            ) : null}
            <td
              className="quiet-cell"
              data-label={EVENT_ATTENDEES_COLUMNS.record}
              data-empty={onRecord ? undefined : ''}
            >
              {onRecord ? (
                <Link
                  className="link"
                  to={
                    onRecord.tracked
                      ? `/people/${onRecord.entity_id}`
                      : `/knowledge?entity=${encodeURIComponent(onRecord.entity_id)}`
                  }
                >
                  {onRecord.display_name}
                </Link>
              ) : null}
            </td>
            <td className="quiet-cell" data-label={EVENT_ATTENDEES_COLUMNS.status}>
              {row.needs_review
                ? EVENT_ATTENDEE_NEEDS_REVIEW
                : row.on_record_rated && row.status !== 'promoted'
                  ? EVENT_ATTENDEE_RATED_FROM_RECORD
                  : EVENT_ATTENDEE_STATUS_LABELS[row.status]}
            </td>
          </tr>
        );
      })}
    </RecordTable>
  );
}
